import type { TimetableAct } from './schemas/edition.js';
import { TimetableActSchema } from './schemas/edition.js';

export const TIMETABLE_CSV_HEADERS = [
  'id',
  'titleDe',
  'titleEn',
  'titleCs',
  'stageId',
  'startsAt',
  'endsAt',
  'descriptionDe',
  'descriptionEn',
  'descriptionCs',
  'tags',
  'imageAssetId',
] as const;

export type TimetableCsvHeader = (typeof TIMETABLE_CSV_HEADERS)[number];

export type TimetableImportError = {
  code: string;
  message: string;
  row?: number;
  field?: string;
};

export type TimetableImportResult = {
  ok: boolean;
  acts: TimetableAct[];
  errors: TimetableImportError[];
};

type CsvParseResult =
  | { ok: true; rows: string[][] }
  | { ok: false; error: TimetableImportError };

function error(code: string, message: string, row?: number, field?: string): TimetableImportError {
  const base = { code, message };
  if (row === undefined && field === undefined) {
    return base;
  }
  if (field === undefined) {
    return row === undefined ? base : { ...base, row };
  }
  if (row === undefined) {
    return { ...base, field };
  }
  return { ...base, row, field };
}

function parseCsv(input: string): CsvParseResult {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];
    if (character === undefined) {
      continue;
    }

    if (inQuotes) {
      if (character === '"') {
        if (input[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === '"' && field.length === 0) {
      inQuotes = true;
    } else if (character === ',') {
      row.push(field);
      field = '';
    } else if (character === '\n') {
      row.push(field.endsWith('\r') ? field.slice(0, -1) : field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += character;
    }
  }

  if (inQuotes) {
    return { ok: false, error: error('csv.unclosed_quote', 'CSV contains an unclosed quoted field.') };
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field.endsWith('\r') ? field.slice(0, -1) : field);
    rows.push(row);
  }

  return { ok: true, rows };
}

function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed.length === 0 ? undefined : trimmed;
}

function normaliseJsonRow(input: unknown): unknown {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return input;
  }

  const record = input as Record<string, unknown>;
  if (typeof record.title === 'string') {
    return { ...record, title: { de: record.title } };
  }
  return input;
}

function addParsedAct(
  input: unknown,
  rowNumber: number,
  acts: TimetableAct[],
  errors: TimetableImportError[],
): boolean {
  const parsed = TimetableActSchema.safeParse(input);
  if (!parsed.success) {
    parsed.error.issues.forEach((issue) => {
      const field = issue.path.map(String).join('.');
      errors.push(error(`schema.${issue.code}`, issue.message, rowNumber, field.length > 0 ? field : undefined));
    });
    return false;
  }
  acts.push(parsed.data);
  return true;
}

function addDuplicateErrors(
  acts: readonly TimetableAct[],
  sourceRows: readonly number[],
  errors: TimetableImportError[],
): void {
  const seen = new Map<string, number>();
  acts.forEach((act, index) => {
    const sourceRow = sourceRows[index] ?? index + 1;
    const previous = seen.get(act.id);
    if (previous === undefined) {
      seen.set(act.id, sourceRow);
      return;
    }
    errors.push(
      error(
        'duplicate_id',
        `Act ID "${act.id}" is duplicated (first imported at row ${previous}).`,
        sourceRow,
        'id',
      ),
    );
  });
}

export function importTimetableJson(input: string | unknown): TimetableImportResult {
  let value: unknown = input;
  if (typeof input === 'string') {
    try {
      value = JSON.parse(input) as unknown;
    } catch {
      const errors = [error('json.invalid', 'Timetable JSON could not be parsed.')];
      return { ok: false, acts: [], errors };
    }
  }

  let rows: unknown;
  if (Array.isArray(value)) {
    rows = value;
  } else if (typeof value === 'object' && value !== null && 'timetable' in value) {
    rows = (value as { timetable?: unknown }).timetable;
  }

  if (!Array.isArray(rows)) {
    const errors = [error('json.expected_array', 'Expected an array or an object with a timetable array.')];
    return { ok: false, acts: [], errors };
  }

  const acts: TimetableAct[] = [];
  const errors: TimetableImportError[] = [];
  const sourceRows: number[] = [];
  rows.forEach((row, index) => {
    if (addParsedAct(normaliseJsonRow(row), index + 1, acts, errors)) {
      sourceRows.push(index + 1);
    }
  });
  addDuplicateErrors(acts, sourceRows, errors);
  return { ok: errors.length === 0, acts, errors };
}

export function importTimetableCsv(input: string): TimetableImportResult {
  const parsedCsv = parseCsv(input);
  if (!parsedCsv.ok) {
    return { ok: false, acts: [], errors: [parsedCsv.error] };
  }

  const nonBlankRows = parsedCsv.rows.filter((row) => row.some((field) => field.trim().length > 0));
  const rawHeader = nonBlankRows[0];
  if (rawHeader === undefined) {
    const errors = [error('csv.empty', 'CSV is empty.')];
    return { ok: false, acts: [], errors };
  }

  const header = rawHeader.map((field, index) => {
    const trimmed = field.trim();
    return index === 0 ? trimmed.replace(/^\uFEFF/, '') : trimmed;
  });
  const headerIndex = new Map(header.map((name, index) => [name, index]));
  const titleHeader = headerIndex.has('titleDe') ? 'titleDe' : 'title';
  const required = ['id', titleHeader, 'stageId', 'startsAt', 'endsAt'];
  const errors: TimetableImportError[] = [];

  required.forEach((requiredHeader) => {
    if (!headerIndex.has(requiredHeader)) {
      errors.push(error('csv.missing_header', `Missing required CSV header "${requiredHeader}".`, 1, requiredHeader));
    }
  });
  if (errors.length > 0) {
    return { ok: false, acts: [], errors };
  }

  const acts: TimetableAct[] = [];
  const sourceRows: number[] = [];
  const valueAt = (row: readonly string[], name: string): string | undefined => {
    const index = headerIndex.get(name);
    return index === undefined ? undefined : nonEmpty(row[index]);
  };

  nonBlankRows.slice(1).forEach((row, rowIndex) => {
    const titleDe = valueAt(row, titleHeader);
    const titleEn = valueAt(row, 'titleEn');
    const titleCs = valueAt(row, 'titleCs');
    const descriptionDe = valueAt(row, 'descriptionDe');
    const descriptionEn = valueAt(row, 'descriptionEn');
    const descriptionCs = valueAt(row, 'descriptionCs');
    const rawTags = valueAt(row, 'tags');
    const imageAssetId = valueAt(row, 'imageAssetId');

    const title = {
      de: titleDe ?? '',
      ...(titleEn === undefined ? {} : { en: titleEn }),
      ...(titleCs === undefined ? {} : { cs: titleCs }),
    };
    const hasDescription = descriptionDe !== undefined;
    const description = hasDescription
      ? {
          de: descriptionDe,
          ...(descriptionEn === undefined ? {} : { en: descriptionEn }),
          ...(descriptionCs === undefined ? {} : { cs: descriptionCs }),
        }
      : undefined;
    const tags = rawTags
      ?.split('|')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    const candidate = {
      id: valueAt(row, 'id') ?? '',
      title,
      stageId: valueAt(row, 'stageId') ?? '',
      startsAt: valueAt(row, 'startsAt') ?? '',
      endsAt: valueAt(row, 'endsAt') ?? '',
      ...(description === undefined ? {} : { description }),
      ...(tags === undefined || tags.length === 0 ? {} : { tags }),
      ...(imageAssetId === undefined ? {} : { imageAssetId }),
    };

    if (addParsedAct(candidate, rowIndex + 2, acts, errors)) {
      sourceRows.push(rowIndex + 2);
    }
  });

  addDuplicateErrors(acts, sourceRows, errors);
  return { ok: errors.length === 0, acts, errors };
}
